import { useState } from "react";
import { SmartphoneIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@ui/dialog";

import { useSaveSadieVoicePhoneCall } from "@/hooks/mutations/useSaveSadieVoicePhoneCall";
import { SadieVoiceSipRequest, SadieVoiceSipSetup } from "@/types";

// Saves the Live setup in guac so Sadie can be called from a phone.
export const SadieVoicePhoneCallButton = ({
  disabled,
  inboundCallSetup,
}: {
  disabled: boolean;
  inboundCallSetup: () => SadieVoiceSipRequest;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: savePhoneCall, isPending } = useSaveSadieVoicePhoneCall();

  /* React Hooks */
  // Kept after closing so the content doesn't vanish mid fade-out.
  const [saved, setSaved] = useState<SadieVoiceSipSetup | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  /* Handlers */
  const handleSave = () => {
    // A failure shows guac's global error banner, and the modal stays shut.
    savePhoneCall(inboundCallSetup(), {
      onSuccess: (setup) => {
        setSaved(setup);
        setIsOpen(true);
      },
    });
  };

  /* Render */
  return (
    <>
      <Button
        disabled={disabled || isPending}
        onClick={handleSave}
        size="sm"
        variant="outline"
      >
        <SmartphoneIcon className="mr-1.5 h-4 w-4" />
        {isPending ? "Saving…" : "Save For Phone Call"}
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Setup saved</DialogTitle>
          </DialogHeader>
          {saved ? (
            <Column className="gap-3">
              <Column className="gap-1">
                <Text className="text-sm text-slate-500">Call Sadie at</Text>
                <Text className="font-mono text-2xl font-semibold text-slate-800">
                  {saved.phoneNumber}
                </Text>
              </Column>
              <Text className="text-sm font-normal text-slate-600">
                Every call to the number uses the latest save.
              </Text>
            </Column>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
